import { Box, useTheme } from '@mui/material';
import React from 'react';

import {
  Dropdown,
  DropdownListItem,
  DropdownProps,
  TextField,
  TextFieldProps,
  Tooltip,
} from '@verantos-dev/shared-components';

export interface CohortBuilderEditModalSummaryProps<InputType extends string> {
  id: string;
  nameProps: Omit<
    TextFieldProps,
    | 'id'
    | 'displayNumCharacter'
    | 'excludeLeadTailSpace'
    | 'beepMaxCharacters'
    | 'beepInvalidCharacters'
    | 'showErrorMsg'
  > & {
    ref?: React.MutableRefObject<HTMLInputElement | null>;
  };
  typeProps: Omit<DropdownProps, 'options' | 'value'> & {
    title: string;
    ref?: React.MutableRefObject<HTMLSelectElement | null>;
    options: InputType[] | undefined;
    value: InputType | string;
  };
  viewMode: boolean;
  isCohortBuilderItemInUse?: boolean;
}

export const NAME_VALID_CHARACTERS = /[a-z0-9><=+\-_ ~`!@#$%^&*(){}[\]|\\/:;'",.?]/i;
export const NAME_VALID_CHARACTERS_WITHOUT_DOUBLE_QUOTE =
  /[a-z0-9><=+\-_ ~`!@#$%^&*(){}[\]|\\/:;',.?]/i;
export const NAME_MAX_CHARACTER_LENGTH = 60;

export const CohortBuilderEditModalSummary = <InputType extends string>({
  id,
  viewMode,
  nameProps,
  typeProps: { title: typeTitleText, options, ...typeProps },
  isCohortBuilderItemInUse,
}: CohortBuilderEditModalSummaryProps<InputType>) => {
  const theme = useTheme();

  const typeDropdownOptions = (): DropdownListItem[] => {
    return options?.map((item) => ({ value: item, displayValue: item })) || [];
  };

  return (
    <Box
      sx={
        viewMode
          ? {
              backgroundColor: theme.palette.cardBackground?.main,
              px: theme.spacing(4),
              pb: theme.spacing(4),
              borderRadius: theme.spacing(1),
              mb: theme.spacing(2),
              spacing: theme.spacing(2),
            }
          : {
              mb: theme.spacing(2),
              spacing: theme.spacing(2),
            }
      }
    >
      <Box
        data-testid={`cohortBuilderEditModalSummary-${id}`}
        sx={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'space-start',
          alignItems: 'start',
          gap: theme.spacing(4),
          pt: theme.spacing(4),
          width: '100%',
        }}
      >
        <TextField
          width={380}
          displayNumCharacter={true}
          excludeLeadTailSpace={true}
          showErrorMsg={false}
          beepMaxCharacters={true}
          beepInvalidCharacters={true}
          maxCharacters={NAME_MAX_CHARACTER_LENGTH}
          validCharacters={NAME_VALID_CHARACTERS_WITHOUT_DOUBLE_QUOTE}
          {...nameProps}
          id="cohort-builder-summary-name"
          displayLabel={nameProps.displayLabel ?? 'Name'}
          placeholder={nameProps.placeholder ?? 'Enter name'}
          helperText={nameProps.helperText}
          viewMode={viewMode}
        />
        <Tooltip
          id="cohort-builder-summary-type-select-tooltip"
          title={typeTitleText}
          arrow
          width={'auto'}
        >
          <Dropdown
            width={220}
            {...typeProps}
            id={'cohort-builder-summary-type-select'}
            label={typeProps.label ?? 'Type'}
            placeholder={typeProps.placeholder ?? 'Select type'}
            viewMode={viewMode}
            options={typeDropdownOptions()}
            readOnly={isCohortBuilderItemInUse || !nameProps.value}
            aria-readonly={isCohortBuilderItemInUse}

            // onChange={onTypeChange}
          />
        </Tooltip>
      </Box>
    </Box>
  );
};
